import { ArrayUtils, engineComponent } from "neo-common";
import { Communication } from "neo-games";
import { ComponentTypes } from "../../Components/ComponentTypes";
import { CreateFrameTimer, FrameTimerEventEmitter, FrameTimerEvents } from "../../Utils/FrameTimer";
import { GameManager } from "../../Managers/GameManager";
import { PickController } from "./PickController";
import { ContainerElement } from "../../Components/Elements/Graphics/ContainerElement";
import { PlayList } from "../../Components/Sounds/PlayList";
import { HtmlController } from "../../HTMLComponents/HtmlController";
import { StageController } from "../../Components/StageController";
import { AmbientBackground } from "../../Utils/AmbientBackground";
import { WinAnimatorPick } from "../../CustomComponents/WinAnimatorPick";
import { SwitchElement } from "../../Components/Elements/Graphics/SwitchElement";
import { BaseView } from "../Base/BaseView";
import { LayoutContainerElement } from "../../CustomComponents/LayoutContainerElement";
import { Game } from "neo-games/Engine/Communication/index";
import play = PIXI.sound.play;

@engineComponent(ComponentTypes.DisplayObject)
export class PickView extends LayoutContainerElement {
    private static readonly NextPickTransitionDuration: number = 15;
    private static readonly FirstRoundSoundStart: number = 60;
    private static readonly NextRoundSoundStart: number = 120;

    private _controller: PickController;

    private _introDelay: FrameTimerEventEmitter;
    private _outroDelay: FrameTimerEventEmitter;
    private _bonusTable: HTMLElement;

    private _winAnimator: WinAnimatorPick;

    private _nextPickTransitionDelay: FrameTimerEventEmitter;
    private _firstRoundSoundDelay: FrameTimerEventEmitter;
    private _nextRoundSoundDelay: FrameTimerEventEmitter;

    constructor() {
        super();

        this._introDelay = CreateFrameTimer("introDelay");
        this._introDelay.on(FrameTimerEvents.Complete, this.introFinished.bind(this));

        this._outroDelay = CreateFrameTimer("outroDelay");
        this._outroDelay.on(FrameTimerEvents.Complete, this.outroFinished.bind(this));

        this._nextPickTransitionDelay = CreateFrameTimer("nextPickTransitionDelay");
        this._nextPickTransitionDelay.on(FrameTimerEvents.Complete, this.nextPickTransitionFinished.bind(this));

        this._firstRoundSoundDelay = CreateFrameTimer("firstRoundSoundDelay");
        this._firstRoundSoundDelay.on(FrameTimerEvents.Complete, this.firstRoundSound.bind(this));

        this._nextRoundSoundDelay = CreateFrameTimer("nextRoundSoundDelay");
        this._nextRoundSoundDelay.on(FrameTimerEvents.Complete, this.nextRoundSound.bind(this));

        StageController.pickStageContainer = this;
    }

    createByNode(node: Element): void {
        super.createByNode(node);

        if (this.childs != null) {
            this._winAnimator = this.childs["WinAnimator"] as WinAnimatorPick;
        }

        this._bonusTable = document.getElementById("bonusTable");
    }

    initReceived(controller: PickController): void {
        if (DEBUG) {
            this.log("initReceived");
        }

        if (this._winAnimator != null) {
            this._winAnimator.on(WinAnimatorPick.ShowWinValueFinished, this.winValueAnimationFinished.bind(this));
            this._winAnimator.on(
                WinAnimatorPick.ShowPickMultiplierFinished,
                this.multiplierAnimationFinished.bind(this),
            );
            this._winAnimator.on(WinAnimatorPick.ShowFinalTableFinished, this.finalTableAnimationFinished.bind(this));
            this._winAnimator.initializePick(this.getSpots());
        }

        this._controller = controller;
        this.visible = false;
    }

    private getSpots(): Array<SwitchElement> {
        let result: Array<SwitchElement> = [];

        if (this.childs != null) {
            let valueSpots = this.childs["PickValue"] as ContainerElement;
            let multiplierSpots = this.childs["PickMultiplier"] as ContainerElement;

            if (valueSpots != null && multiplierSpots != null) {
                let spotName = "Spot_";

                for (let i = 0; i < 7; i++) {
                    //7 spots
                    if (i < 5) {
                        result.push(valueSpots.childs[spotName + i]);
                    } else {
                        result.push(multiplierSpots.childs[spotName + i]);
                    }
                }
            }
        }

        return result;
    }

    disableLogo(): void {}

    initIntro(): void {
        this.visible = true;
        StageController.baseStage = false;
        StageController.baseStageContainer.visible = false;
        StageController.freespinsStageContainer.visible = false;

        if (this.childs["Background"] != null) this.childs["Background"].goToCase("NORMAL");
        PlayList.loop("SND_BonusLoop");

        AmbientBackground.changeAmbientBackground("pick", 0, 0);

        this._winAnimator.showInitPick(GameManager.pickResponse.round);
        this._winAnimator.showScore(true);
        this._winAnimator.showFinalTable(true);
    }

    collect(): void {}

    start(): void {}

    /**
     *
     * @param spotId
     */
    select(spotId: number): void {
        this._firstRoundSoundDelay.stop();
        this._nextRoundSoundDelay.stop();
        if (GameManager.pickResponse.round.remainingPicks === 2) {
            PlayList.play("SND_BonusReveal");
        } else {
            PlayList.play("SND_Bonus_Butterfly_Reveal");
        }
        if (this.childs["Background"] != null) this.childs["Background"].goToCase("PICK");
    }

    end(): void {}

    startReceived(): void {}

    selectReceived(): void {
        if (
            GameManager.pickResponse != null &&
            GameManager.pickResponse.round != null &&
            GameManager.pickResponse.round.spots != null
        ) {
            this._winAnimator.showPickWins(GameManager.pickResponse.round);
        }
    }

    private continueToMultiplierScene(): void {
        PlayList.play("SND_BonusTransEggOut");
        this._winAnimator.hidePickValueSpots();
        this._controller.continue();

        this._nextPickTransitionDelay.start(PickView.NextPickTransitionDuration);
    }

    private nextPickTransitionFinished(): void {
        if (this.childs["Background"] != null) this.childs["Background"].goToCase("UNPICK");
        this._nextRoundSoundDelay.start(PickView.NextRoundSoundStart);
        this.continueToMultiplierSceneFinished();
    }

    private firstRoundSound(): void {
        PlayList.play("SND_BonusPickMeEggs1-5");
        this._firstRoundSoundDelay.stop();
        this._firstRoundSoundDelay.start(120);
    }

    private nextRoundSound(): void {
        PlayList.play("SND_Bonus_Butterfly_in");
        this._nextRoundSoundDelay.stop();
        this._nextRoundSoundDelay.start(PickView.NextRoundSoundStart);
    }

    private continueToMultiplierSceneFinished(): void {
        this._controller.continueFinished();
    }

    private winValueAnimationFinished(): void {
        this.continueToMultiplierScene();
    }

    private multiplierAnimationFinished(): void {
        this._winAnimator.showFinalTable();
    }

    private finalTableAnimationFinished(): void {
        PlayList.loop("SND_BonusWait");
        this._controller.winCalculationFinished();
    }

    endReceived(): void {
        //PlayList.play('SND_FSWin');
        //PlayList.stop('SND_FSpickupMusic');
        //PlayList.loop('SND_BonusLoop');
    }

    finishWinCalculation(): void {
        if (GameManager.pickResponse.round.remainingPicks === 1) {
            PlayList.stop("SND_BonusCreditboxLoop");
            PlayList.play("SND_BonusCreditboxEnd");
        } else {
            PlayList.stop("SND_BonusMessageCreditboxLoop");
            PlayList.play("SND_BonusMessageCreditboxEnd");
        }

        this._controller.winCalculationFinished();
    }

    finishWinAnimator(): void {
        this._controller.allWinsFinished();
        if (GameManager.pickResponse.round.remainingPicks === 1) {
            PlayList.play("SND_BonusTransEggOut");
        } else {
        }
    }

    intro(long: boolean = false): void {
        StageController.pickStage = true;
        StageController.baseStageContainer.visible = false;
        StageController.freespinsStageContainer.visible = false;
        this.visible = true;

        this.addChild(BaseView.Transition);
        if (this.childs["Background"] != null) this.childs["Background"].goToCase("NORMAL");

        AmbientBackground.changeAmbientBackground("pick", 100, 250);

        this._introDelay.start(15);
    }

    introFinished(): void {
        StageController.baseStage = false;
        StageController.pickStage = true;
        PlayList.loop("SND_BonusLoop");
        this._firstRoundSoundDelay.start(PickView.FirstRoundSoundStart);

        this._winAnimator.showScore();
        this._winAnimator.showInitPick(GameManager.pickResponse.round);
        this._controller.introFinished();
    }

    outro(): void {
        this.visible = true;
        this.addChild(BaseView.Transition);
        BaseView.Transition.goToCase("START");
        PlayList.play("SND_FeatureTransOut");
        PlayList.stop("SND_BonusWait");

        this._outroDelay.start(120);
    }

    outroFinished(): void {
        this._winAnimator.resetSpots();
        this._outroDelay.stop();
        this.visible = false;
        this._controller.outroFinished();
    }

    enable(): void {}
}



/** WEBPACK FOOTER **
 ** ./Engine/Game/GameComponents/Pick/PickView.ts
 **/